<template>
    <div class="custom-input-number flex align-items-center p-fluid relative gap-2">
        <CustomLabel :name="name" :class="!showLabel ? 'visually-hidden' : ''" :label="label" :required="required"/>
        <InputNumber :id="name" v-model="internalValue" :class="computedClasses"
                     :min="min" :max="max" mode="decimal" :show-buttons="false"
                     :disabled="disabled" @blur="onBlur" @input="handleInput"
                     @keydown="onEsc" @change="handleChange" locale="pl-PL"
                     :minFractionDigits="minDigits" :maxFractionDigits="maxDigits"
                     :placeholder="computedPlaceholder"/>
        <CustomValidationMessage :errorMessage="customErrorMessage" />
    </div>
</template>

<script>
    import InputNumber from "primevue/inputnumber";
    import {useField} from "vee-validate";
    import CustomLabel from "./inner/CustomLabel";
    import CustomValidationMessage from "./inner/CustomValidationMessage";

    export default {
        name: "CustomInputNumber",

        props: {
            name: {
                type: String,
            },
            label: {
                type: String,
            },
            min: {
                type: Number,
                default: 0,
            },
            max: {
                type: Number,
                default: null,
            },
            rules: undefined,
            modelValue: null,
            showLabel: {
                type: Boolean,
                default: false,
            },
            minDigits: {
                type: Number,
                default: null,
            },
            maxDigits: {
                type: Number,
                default: null,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            handleEscEnterBlur: {
                type: Boolean,
                default: false,
            },
            required: {
                type: Boolean,
                default: false,
            },
            placeholder: {
                type: String,
                default: "",
            },
            noPlaceholder: {
                type: Boolean,
                default: false,
            },
            customErrorMessage: {
                type: String,
                default: "",
            },
            useGrouping: {
                type: Boolean,
                default: true,
            }
        },
        emits: ["update:modelValue", "onBlur", "onEsc"],

        data() {
            return {
                value: null
            };
        },

        watch: {
            modelValue: {
                handler() {
                    this.inputValue = this.modelValue;
                },
            },
        },

        computed: {
            internalValue: {
                get() {
                    return this.modelValue;
                },
                set(value) {
                    this.$emit("update:modelValue", value);
                },
            },

            computedClasses() {
                return this.errorMessage ? "p-invalid" : "";
            },

            computedPlaceholder() {
                if (this.noPlaceholder) return "";
                if (this.placeholder === "") return this.$t('message.inputs.inputNumberPlaceholder');
                else return this.placeholder;
            },
        },

        setup(props) {
            const {
                value: inputValue,
                errorMessage,
                handleChange,
                meta,
            } = useField(props.name, props.rules, {
                initialValue: props.modelValue,
            });

            return {
                errorMessage,
                inputValue,
                handleChange,
                meta,
            };
        },

        methods: {
            handleInput(ctx) {
                this.$emit("update:modelValue", ctx.value);
            },

            onBlur() {
                if (this.handleEscEnterBlur) {
                    this.$nextTick(() => {
                        this.$emit("onBlur", this.internalValue);
                    });
                }
            },

            onEsc(event) {
                if (!this.handleEscEnterBlur) {
                    return;
                }
                // esc
                if (event.keyCode === 27) {
                    event.target.blur();
                    this.$emit("onEsc");
                }
                // enter - calls onBlur through native events
                if (event.keyCode === 13) {
                    event.target.blur();
                }
            },
        },

        components: {InputNumber, CustomLabel, CustomValidationMessage},
    };
</script>

<style scoped>
</style>
